/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { recipientListsService } from '../../services/recipient-lists.service';
import { userLookupService } from '../../services/user-lookup.service';
import { personnelLookupService } from '../../services/personnel-lookup.service';
import type { Party } from '../../services/notification-engine.types';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import ActiveTag from '../../components/common/active-tag';
import { isRowActive } from '../../utils/active-state';
import {
  CARD, BackLink, CardHeader, Field, Hero, WarnBanner, RecordMeta,
} from '../../components/notifications/engine-view-ui';

const RecipientListView = () => {
  const { id, isReady, navigateToList, navigateToEdit } = useSecureNavigation({ entity: 'recipient list', basePath: '/recipient-lists' });
  const canUpdate = useCanUpdate(JOB_NAMES.RECIPIENT_LIST);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['recipientList', id],
    queryFn: () => recipientListsService.getById(id!),
    enabled: isReady && !!id,
  });

  // A party stores an id; a person recognises a name. Resolve each kind through
  // its own cached lookup, and fall back to the id only when nothing resolves.
  const partyList = data?.parties || [];
  const userIds = partyList.filter((p) => p.partyType === 'USER').map((p) => p.userId).filter(Boolean) as string[];
  const personnelIds = partyList.filter((p) => p.partyType === 'PERSONNEL').map((p) => p.personnelMasterId).filter(Boolean) as string[];
  const hasNestedList = partyList.some((p) => p.partyType === 'LIST');

  const { data: userNames } = useQuery({
    queryKey: ['userNames', 'recipientListView', userIds.join(',')],
    queryFn: () => userLookupService.getNamesByIds(userIds),
    enabled: userIds.length > 0,
    staleTime: 3e5,
  });
  const { data: personnelNames } = useQuery({
    queryKey: ['personnelNames', 'recipientListView', personnelIds.join(',')],
    queryFn: () => personnelLookupService.getNamesByIds(personnelIds),
    enabled: personnelIds.length > 0,
    staleTime: 3e5,
  });
  const { data: listsPage } = useQuery({
    queryKey: ['recipientLists', 'recipientListViewLookup'],
    queryFn: () => recipientListsService.list({ skip: 0, limit: 200 }),
    enabled: hasNestedList,
    staleTime: 3e5,
  });

  const partyLabel = (p: Party): string => {
    if (p.partyType === 'EXTERNAL') {
      return [p.contact?.email, p.contact?.phone, p.contact?.whatsapp].filter(Boolean).join(' · ') || '-';
    }
    if (p.partyType === 'USER') return userNames?.get(String(p.userId)) || p.userId || '-';
    if (p.partyType === 'PERSONNEL') return personnelNames?.get(String(p.personnelMasterId)) || p.personnelMasterId || '-';
    if (p.partyType === 'LIST') {
      return ((listsPage as any)?.data || []).find((l: any) => l._id === String(p.recipientListId))?.recipientListCode
        || p.recipientListId || '-';
    }
    return p.userId || p.personnelMasterId || p.recipientListId || '-';
  };

  if (isLoading) return <PageSkeleton />;
  if (isError || !data) return (
    <div className="p-4">
      <Message severity="error" text={extractErrorMessage(error, 'Recipient list not found')} className="mb-4 w-full" />
      <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigateToList()} />
    </div>
  );

  const editable = canUpdate && !data.isDelete;
  const parties = data.parties || [];
  const dnc = !!data.isDoNotContact;

  return (
    <div className="mx-auto space-y-3 p-4" data-testid="SCR-RecipientList-View">
      <BackLink label="Recipient Lists" onClick={() => navigateToList()} />

      <Hero
        icon={dnc ? 'ban' : 'users'}
        chip={dnc
          ? 'bg-rose-50 text-rose-500 dark:bg-rose-950/50 dark:text-rose-400'
          : 'bg-indigo-50 text-indigo-500 dark:bg-indigo-950/50 dark:text-indigo-400'}
        code={data.recipientListCode}
        tags={<>
          {dnc
            ? <Tag value="DO NOT CONTACT" severity="danger" className="text-xs px-1.5 py-0.5" />
            : <Tag value="AUDIENCE" severity="secondary" className="text-xs px-1.5 py-0.5" />}
          <ActiveTag row={data} />
        </>}
        description={data.description}
        actions={editable && <Button label="Edit" icon="pi pi-pencil" size="small" severity="secondary" onClick={() => id && navigateToEdit(id)} />}
        banner={dnc && !isRowActive(data) && <WarnBanner text="Deactivated, so it suppresses nobody — everyone on it can still be messaged." />}
      />

      <div className={CARD}>
        <CardHeader icon={dnc ? 'ban' : 'users'} color={dnc ? 'text-rose-400' : 'text-indigo-400'}
          title={`${dnc ? 'Suppressed endpoints' : 'Parties'} (${parties.length})`} />
        <div className="px-4 py-4">
          {parties.length ? (
            <ul className="flex flex-col gap-2">
              {parties.map((p, i) => (
                <li key={i} className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/30">
                  <Tag value={p.partyType} severity="secondary" className="text-xs px-1.5 py-0.5" />
                  <span className="text-sm text-ink">{partyLabel(p)}</span>
                </li>
              ))}
            </ul>
          ) : <span className="text-sm text-gray-400">—</span>}
        </div>
      </div>

      <RecordMeta data={data} extra={<Field label="Used for">{dnc ? 'Do not contact' : 'Audience'}</Field>} />
    </div>
  );
};

export default RecipientListView;
